/**
 * LIGHTS ON SLOPED CEILINGS (2026-11-02, Matt: "make sure lights that are supposed to host onto
 * ceilings follow their slopes. That counts for recessed lighting and hung lighting that has
 * mounting plates. Obviously pendants and chandeliers should hang with gravity, not 90 degrees to
 * the pitch of the ceiling").
 *
 * The 3D instance carries only a position and a turn about vertical, so the tilt is BUILT INTO the
 * fixture's prototype: generate3d works out the ceiling's slope at the light (its gradient, in the
 * light's own turned frame) and passes it on the instance's element as `__ceilTilt`; the proto key
 * forks on it; the builders rotate the mesh. What tilts:
 *  - a light that mounts flat to the ceiling (recessed cans, flush and semi-flush mounts, ceiling
 *    devices): all of it — its axis square to the ceiling;
 *  - a hanging light (pendant, chandelier): only its CANOPY, the band right at the ceiling; the
 *    stem / chain and the body below stay plumb.
 */
import type { ParamValue } from '@aj/bim-kernel'

export const CEILING_TILT_PARAM = '__ceilTilt'

/** the kinds that hang plumb below a canopy */
const HANGING = new Set(['pendant', 'chandelier'])

/** the canopy band below the ceiling (feet) — every canopy is at most ~1.2" deep */
export const CANOPY_BAND_FT = 0.11

/** the light's slope gradient in its own frame: [dz/dx, dz/dy] of the ceiling, or null when flat */
export function ceilingTiltOf(params: Record<string, ParamValue> | null | undefined): [number, number] | null {
  const t = params?.[CEILING_TILT_PARAM] as unknown
  if (!Array.isArray(t) || t.length !== 2) return null
  const gx = Number(t[0]), gy = Number(t[1])
  if (!Number.isFinite(gx) || !Number.isFinite(gy) || Math.hypot(gx, gy) < 1e-4) return null
  return [gx, gy]
}

/** the gradient of a world-plan slope (rise per foot `m` toward unit `n`) in a light turned by
 *  `rotZ`, rounded so lights on one ceiling share a prototype; null when flat */
export function localCeilingTilt(m: number, n: [number, number], rotZ: number): [number, number] | null {
  const gx = m * n[0], gy = m * n[1]
  const c = Math.cos(rotZ), s = Math.sin(rotZ)
  const lx = Math.round((gx * c + gy * s) * 1000) / 1000
  const ly = Math.round((-gx * s + gy * c) * 1000) / 1000
  return Math.hypot(lx, ly) < 1e-4 ? null : [lx, ly]
}

/**
 * Rotate a built mesh (positions + normals, flat arrays) so its up axis is square to the ceiling.
 * `kind` decides what tilts: a hanging kind only its canopy band (z ≥ −CANOPY_BAND_FT), anything
 * else all of it. The rotation is about the mount point (the origin, on the ceiling).
 */
export function tiltForCeiling(mesh: { pos: number[]; nrm?: number[] }, tilt: [number, number], kind: string): void {
  const [gx, gy] = tilt
  // the ceiling's upward normal: (−gx, −gy, 1), normalised
  const L = Math.hypot(gx, gy, 1)
  const N = [-gx / L, -gy / L, 1 / L]
  // rotate +Z onto N: axis z × N, angle acos(N.z)
  let kx = -N[1], ky = N[0]
  const kl = Math.hypot(kx, ky)
  if (kl < 1e-9) return
  kx /= kl; ky /= kl
  const cos = N[2], sin = Math.sqrt(Math.max(0, 1 - cos * cos))
  const rot = (x: number, y: number, z: number): [number, number, number] => {
    // Rodrigues, k = (kx, ky, 0)
    const kdot = kx * x + ky * y
    const cx = ky * z, cy = -kx * z, cz = kx * y - ky * x   // k × v
    return [
      x * cos + cx * sin + kx * kdot * (1 - cos),
      y * cos + cy * sin + ky * kdot * (1 - cos),
      z * cos + cz * sin,
    ]
  }
  const canopyOnly = HANGING.has(kind)
  const P = mesh.pos, Nm = mesh.nrm
  for (let i = 0; i < P.length; i += 3) {
    if (canopyOnly && P[i + 2] < -CANOPY_BAND_FT) continue
    const r = rot(P[i], P[i + 1], P[i + 2])
    P[i] = r[0]; P[i + 1] = r[1]; P[i + 2] = r[2]
    if (Nm) {
      const q = rot(Nm[i], Nm[i + 1], Nm[i + 2])
      Nm[i] = q[0]; Nm[i + 1] = q[1]; Nm[i + 2] = q[2]
    }
  }
}
